import { __assign } from "tslib";
import { mapValues, set } from 'lodash-es';
import PropTypes from 'prop-types';
import React from 'react';
import Extension from '../../containers/handoff/Extension';
import Guide from '../../containers/handoff/Guide';
import { getPercentageValue } from '../../utils/handoff/guide';
var measurements = [
    {
        hoveredSide: 'top',
        selectedSide: 'top',
        shouldRender: function (guides) {
            return !guides.top.bottom.guide && !guides.bottom.top.guide;
        },
    },
    {
        hoveredSide: 'top',
        selectedSide: 'bottom',
    },
    {
        hoveredSide: 'bottom',
        selectedSide: 'top',
    },
    {
        hoveredSide: 'bottom',
        selectedSide: 'bottom',
        shouldRender: function (guides) {
            return !guides.top.bottom.guide && !guides.bottom.top.guide;
        },
    },
    {
        hoveredSide: 'right',
        selectedSide: 'right',
        shouldRender: function (guides) {
            return !guides.right.left.guide && !guides.left.right.guide;
        },
    },
    {
        hoveredSide: 'right',
        selectedSide: 'left',
    },
    {
        hoveredSide: 'left',
        selectedSide: 'right',
    },
    {
        hoveredSide: 'left',
        selectedSide: 'left',
        shouldRender: function (guides) {
            return !guides.right.left.guide && !guides.left.right.guide;
        },
    },
];
export default function RelativeGuides(_a) {
    var selectedElement = _a.selectedElement, hoveredElement = _a.hoveredElement, relativeElement = _a.relativeElement, pixelRatio = _a.pixelRatio;
    var allGuides = {};
    measurements.forEach(function (_a) {
        var hoveredSide = _a.hoveredSide, selectedSide = _a.selectedSide;
        var measurement = getMeasurement({
            selected: selectedElement,
            hovered: hoveredElement,
            selectedElementSide: selectedSide,
            hoveredElementSide: hoveredSide,
        });
        var guide = attachGuide({
            selected: selectedElement,
            hovered: hoveredElement,
            measurement: measurement,
        });
        var extension;
        if (guide) {
            var totalLength = measurement.isVertical
                ? relativeElement.height
                : relativeElement.width;
            guide.pctValue = getPercentageValue(guide.value, totalLength);
            if (guide.attachedTo === 'selected') {
                extension = getExtension(hoveredElement, hoveredSide, guide);
            }
            else {
                extension = getExtension(selectedElement, selectedSide, guide);
            }
        }
        set(allGuides, [hoveredSide, selectedSide], {
            guide: guide,
            extension: extension,
        });
    });
    var renderedGuides = [];
    measurements.forEach(function (_a) {
        var hoveredSide = _a.hoveredSide, selectedSide = _a.selectedSide, shouldRender = _a.shouldRender;
        var guide = allGuides[hoveredSide][selectedSide];
        if (guide.guide) {
            if (!shouldRender || shouldRender(allGuides)) {
                renderedGuides.push(React.createElement(Guide, __assign({ key: "guide-".concat(hoveredSide, "-").concat(selectedSide) }, mapValues(guide.guide, function (v) { return (v ? v * pixelRatio : v); }), { pctValue: guide.guide.pctValue, value: guide.guide.value })));
                if (guide.extension) {
                    renderedGuides.push(React.createElement(Extension, __assign({ key: "extension-".concat(hoveredSide, "-").concat(selectedSide), side: guide.extension.side }, mapValues(guide.extension.layout, function (v) {
                        return v ? v * pixelRatio : v;
                    }))));
                }
            }
        }
    });
    return React.createElement("div", null, renderedGuides);
}
RelativeGuides.propTypes = {
    selectedElement: PropTypes.object.isRequired,
    hoveredElement: PropTypes.object.isRequired,
    relativeElement: PropTypes.object.isRequired,
    pixelRatio: PropTypes.number.isRequired,
};
function attachGuide(_a) {
    var selected = _a.selected, hovered = _a.hovered, measurement = _a.measurement;
    var guideAttachedToSelectedElement = attachGuideTo({
        element: selected,
        measurement: measurement,
    });
    if (!isLineThroughBoundingBox({
        isVertical: measurement.isVertical,
        start: guideAttachedToSelectedElement,
        length: measurement.length,
        bbox: selected,
    })) {
        return __assign(__assign({}, guideAttachedToSelectedElement), { attachedTo: 'selected', isVertical: measurement.isVertical });
    }
    var guideAttachedToHoveredElement = attachGuideTo({
        element: hovered,
        measurement: measurement,
    });
    if (!isLineThroughBoundingBox({
        isVertical: measurement.isVertical,
        start: guideAttachedToHoveredElement,
        length: measurement.length,
        bbox: hovered,
    })) {
        return __assign(__assign({}, guideAttachedToHoveredElement), { attachedTo: 'hovered', isVertical: measurement.isVertical });
    }
    return null;
}
function attachGuideTo(_a) {
    var element = _a.element, measurement = _a.measurement;
    var line = measurement.isVertical
        ? {
            top: measurement.start,
            left: element.left + element.width / 2,
            height: measurement.length,
        }
        : {
            top: element.top + element.height / 2,
            left: measurement.start,
            width: measurement.length,
        };
    return __assign(__assign({}, line), { value: measurement.length });
}
function getMeasurement(_a) {
    var selected = _a.selected, hovered = _a.hovered, selectedElementSide = _a.selectedElementSide, hoveredElementSide = _a.hoveredElementSide;
    var hoveredCoord = hovered[hoveredElementSide];
    var selectedCoord = selected[selectedElementSide];
    var distance = hoveredCoord - selectedCoord;
    var absDistance = Math.abs(distance);
    var base;
    if (distance < 0) {
        base = hoveredCoord;
    }
    else {
        base = selectedCoord;
    }
    return {
        start: base,
        length: absDistance,
        isVertical: isVerticalMeasurement(selectedElementSide),
    };
}
function getExtension(selectedElement, selectedElementSide, guide) {
    var _a;
    var overDraw = 50;
    var adjacentSide;
    var fixedProp;
    var sizeProp;
    var position;
    var size;
    if (guide.isVertical) {
        adjacentSide = 'left';
        fixedProp = 'top';
        sizeProp = 'width';
    }
    else {
        adjacentSide = 'top';
        fixedProp = 'left';
        sizeProp = 'height';
    }
    if (guide[adjacentSide] < selectedElement[adjacentSide]) {
        position = Math.max(0, guide[adjacentSide] - overDraw);
        size = selectedElement[adjacentSide] - position;
    }
    else if (guide[adjacentSide] > selectedElement[getOppositeSide(adjacentSide)]) {
        position = selectedElement[getOppositeSide(adjacentSide)];
        size =
            guide[adjacentSide] -
                selectedElement[getOppositeSide(adjacentSide)] +
                overDraw;
    }
    var props = (_a = {},
        _a[fixedProp] = selectedElement[selectedElementSide],
        _a[adjacentSide] = position,
        _a[sizeProp] = size,
        _a.isVertical = !guide.isVertical,
        _a);
    return { side: selectedElementSide, layout: props };
}
function getOppositeSide(side) {
    return {
        left: 'right',
        right: 'left',
        top: 'bottom',
        bottom: 'top',
    }[side];
}
function isVerticalMeasurement(side) {
    return side === 'top' || side === 'bottom';
}
function isLineThroughBoundingBox(_a) {
    var isVertical = _a.isVertical, start = _a.start, length = _a.length, bbox = _a.bbox;
    if (isVertical) {
        if (start.left >= bbox.left && start.left <= bbox.right) {
            return isOverlapping([start.top, start.top + length], [bbox.top, bbox.bottom]);
        }
    }
    else if (start.top >= bbox.top && start.top <= bbox.bottom) {
        return isOverlapping([start.left, start.left + length], [bbox.left, bbox.right]);
    }
    return false;
}
function isOverlapping(range1, range2) {
    var isOutside = range1[1] <= range2[0] || range1[0] >= range2[1];
    return !isOutside;
}
